import { all, takeEvery, put, call, select } from "redux-saga/effects";
import { notification } from "antd";
import { tekst } from "../../utils/tlumacz";

import actions from "./actions";
import {
    SentDeklaracjaCreateInterface,
    SentDeklaracjaInterface,
    SentPojazdInterface
} from "./sent.type";
import {
    getSentDeklaracjeApi,
    getSentPojazdyApi,
    postSentDeklaracjaApi,
    deleteSentDeklaracjaApi
} from "../../services/sent.service";
import { ReducerType } from "../reducer.type";
import { WorkspaceType } from "../user/user.type";
import logError from "../../utils/logError";

/**
 * Sciezki SENT sa zakresowane obszarem, wiec potrzebny jest jego uuid.
 *
 * Ktory selektor: zgodnie z regula z docs/03-wzorce.md — `state.client.owner
 * .workspace` tylko dla zakladek NA EKRANIE KLIENTA, wszystko inne bierze
 * `state.user.activeWorkspace`. Deklaracje SENT to samodzielny ekran w
 * sekcji Raporty, wiec obowiazuje wariant drugi (jak w module `devices`).
 */
const getWorkspace = (state: ReducerType) => state.user.activeWorkspace;

/**
 * Zwraca uuid obszaru albo pusty ciag.
 *
 * Bez tego sprawdzenia przy braku aktywnego obszaru poleciałoby zadanie pod
 * `/api/v1/workspaces/undefined/sent` — czyli 404 opisany w konsoli jako blad
 * serwera, choc przyczyna jest po naszej stronie. Zdarza sie to realnie:
 * administrator platformy przed wejsciem w konto klienta nie ma aktywnego
 * obszaru.
 */
function* uuidObszaru(kontekst: string): any {
    const workspace: WorkspaceType = yield select(getWorkspace);
    const uuid = workspace && workspace.uuid;
    if (!uuid) {
        logError(kontekst, new Error("brak aktywnego obszaru — nie ma dla kogo pobrac danych SENT"));
        return "";
    }
    return uuid;
}

export function* GET_SENT_DEKLARACJE() {
    yield put({ type: actions.SET_SENT_DEKLARACJE_LOADING });
    try {
        const uuid: string = yield call(uuidObszaru, "redux/sent/sagas/GET_SENT_DEKLARACJE");
        if (!uuid) {
            yield put({ type: actions.SET_SENT_DEKLARACJE_ERROR });
            return;
        }
        const lista: SentDeklaracjaInterface[] = yield call(getSentDeklaracjeApi, uuid);
        // Wysylamy zawsze, takze przy pustej liscie — inaczej ekran zostalby
        // w stanie wczytywania na zawsze.
        yield put({ type: actions.SET_SENT_DEKLARACJE, payload: lista || [] });
    } catch (e) {
        logError("redux/sent/sagas/GET_SENT_DEKLARACJE", e);
        yield put({ type: actions.SET_SENT_DEKLARACJE_ERROR });
    }
}

export function* GET_SENT_POJAZDY() {
    yield put({ type: actions.SET_SENT_POJAZDY_LOADING });
    try {
        const uuid: string = yield call(uuidObszaru, "redux/sent/sagas/GET_SENT_POJAZDY");
        if (!uuid) {
            yield put({ type: actions.SET_SENT_POJAZDY_ERROR });
            return;
        }
        const lista: SentPojazdInterface[] = yield call(getSentPojazdyApi, uuid);
        yield put({ type: actions.SET_SENT_POJAZDY, payload: lista || [] });
    } catch (e) {
        logError("redux/sent/sagas/GET_SENT_POJAZDY", e);
        yield put({ type: actions.SET_SENT_POJAZDY_ERROR });
    }
}

export function* POST_SENT_DEKLARACJA(action: {
    payload: SentDeklaracjaCreateInterface;
    type: string;
}) {
    yield put({ type: actions.SET_SENT_ZAPIS, payload: true });
    try {
        const uuid: string = yield call(uuidObszaru, "redux/sent/sagas/POST_SENT_DEKLARACJA");
        if (!uuid) throw new Error("brak aktywnego obszaru");

        yield call(postSentDeklaracjaApi, uuid, action.payload);
        notification.success({
            message: tekst("sent:added_notification"),
            description: tekst("sent:added_notification_desc")
        });
        // odswiezamy oba zestawy: liste deklaracji i statusy pojazdow
        yield put({ type: actions.GET_SENT_DEKLARACJE });
        yield put({ type: actions.GET_SENT_POJAZDY });
    } catch (e) {
        logError("redux/sent/sagas/POST_SENT_DEKLARACJA", e);
        notification.error({
            message: tekst("sent:error_add"),
            description: tekst("sent:error_add_desc")
        });
    } finally {
        yield put({ type: actions.SET_SENT_ZAPIS, payload: false });
    }
}

export function* DELETE_SENT_DEKLARACJA(action: {
    payload: number | string;
    type: string;
}) {
    yield put({ type: actions.SET_SENT_ZAPIS, payload: true });
    try {
        const uuid: string = yield call(uuidObszaru, "redux/sent/sagas/DELETE_SENT_DEKLARACJA");
        if (!uuid) throw new Error("brak aktywnego obszaru");

        yield call(deleteSentDeklaracjaApi, uuid, action.payload);
        notification.success({
            message: tekst("sent:deleted_notification"),
            description: tekst("sent:deleted_notification_desc")
        });
        yield put({ type: actions.GET_SENT_DEKLARACJE });
        yield put({ type: actions.GET_SENT_POJAZDY });
    } catch (e) {
        logError("redux/sent/sagas/DELETE_SENT_DEKLARACJA", e);
        notification.error({
            message: tekst("sent:error_delete"),
            description: tekst("sent:error_delete_desc")
        });
    } finally {
        yield put({ type: actions.SET_SENT_ZAPIS, payload: false });
    }
}

export default function* RootSaga() {
    yield all([
        takeEvery(actions.GET_SENT_DEKLARACJE, GET_SENT_DEKLARACJE),
        takeEvery(actions.GET_SENT_POJAZDY, GET_SENT_POJAZDY),
        takeEvery(actions.POST_SENT_DEKLARACJA, POST_SENT_DEKLARACJA),
        takeEvery(actions.DELETE_SENT_DEKLARACJA, DELETE_SENT_DEKLARACJA)
    ]);
}
